import React, { useState } from 'react';
import removeFilterValue, { isRangeFilter } from '../../utils/filters';
import useFilterStore from './filterStore';
import { FrontendFilterValue } from '../../types/filters.type';
import FilterSummary from '../filterSummary';
import Button from '../buttons/baseButton';
import PostCreateFilterTemplate from '../../services/adapters/createFilterAdapter';
import customIcons from '../../utils/icons';
import InformationText from '../InformationText';
import useRequestState from '../../hooks/useRequestState';
import { FilterTemplate } from '../../api-models';
import sanitize from '../../utils/formatters';
import useAllFilterStore from '../../shared/hooks/allFilterStore';

export default function FilterTemplateForm({ onClose }: { onClose: () => void }) {
  const [title, setTitle] = useState('');
  const [warning, setWarning] = useState('');
  const { selectedFilters, updateFilterStore } = useFilterStore();
  const { filterTypeLookup } = useAllFilterStore((state) => state);

  const hasSelectedFilters = Object.keys(selectedFilters).length > 0;

  const removeFilter = (category: string, key: string, filterDisplayName: string, value: FrontendFilterValue) => {
    const isRange = isRangeFilter(key, filterTypeLookup);
    const updatedFilters = removeFilterValue(selectedFilters, category, key, filterDisplayName, value, isRange);
    updateFilterStore(updatedFilters);
  };
  const {
    error: createTemplateError,
    handleRequest,
    FallbackWrapper,
  } = useRequestState<FilterTemplate>();

  const createFilterTemplate = async () => {
    const sanitizedTitle = sanitize(title);
    const createFilterAdapter = new PostCreateFilterTemplate();
    const response = createFilterAdapter.adaptResponse({ templateName: sanitizedTitle, selectedFilters });
    await handleRequest(response);
    if (!createTemplateError) {
      onClose();
    }
  };

  const handleTextInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    if (inputValue.length >= 75) {
      setWarning('Template name cannot exceed 75 characters.');
    } else {
      setWarning('');
      setTitle(inputValue);
    }
  };

  const errorMessage = `0 filters selected.
  Please select filters to create a template.`;

  const formError = (
    <div className="flex flex-col items-center justify-center gap-4 p-4 h-full">
      <span className="text-2xl text-pipstatus-alert">{customIcons.errorIcon}</span>
      <span className="text-md text-piptext-body whitespace-pre-line">
        {errorMessage || createTemplateError?.message }
      </span>
    </div>
  );
  const templateForm = (
    <div className="flex flex-col gap-2">
      <div id="modal-header" className="flex items-center text-start gap-2 w-full">
        <i className="mx-4 text-2xl text-primary">{customIcons.fileUp}</i>
        <InformationText title="Create Filter Template" subtitle="" />
      </div>
      <div className="w-full overflow-y-scroll">
        <label htmlFor="filter-template-name" className="text-sm flex flex-col text-start gap-4 m-4 pt-4">
          Template Name
          <input
            key="filter-template-name"
            id="filter-template-name"
            type="text"
            maxLength={75}
            name="filterTemplateName"
            placeholder="Template name"
            className="input-default focus:input-focused"
            value={title}
            onChange={handleTextInputChange}
          />
          {warning && <span className="text-pipstatus-alert">{warning}</span>}

        </label>
        <FilterSummary filters={selectedFilters} onClick={removeFilter} />
      </div>

      <div className="flex justify-evenly items-center w-full">
        <FallbackWrapper loadingMessage="Creating template...">
          <Button
            id="cancel-modal-button"
            label="Cancel"
            className="p-1 m-4 button-default w-full"
            onClick={onClose}
          />
          <Button
            id="submit-filter-template-modal-button"
            label="Submit"
            className="p-1 m-4 w-full button-green"
            onClick={createFilterTemplate}
          />
        </FallbackWrapper>
      </div>
    </div>
  );

  return (hasSelectedFilters ? templateForm : (formError));
}
